import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from './api-client';
import { readPublicAppearance, writePublicAppearance, type Appearance } from './public-appearance';
import { loadSession, subscribeToSession } from './session';

export type { Appearance } from './public-appearance';
export type ThemeColor = 'default' | 'blue' | 'purple' | 'red' | 'pink' | 'orange' | 'yellow' | 'teal';

export const THEME_COLORS: readonly ThemeColor[] = ['default', 'blue', 'purple', 'red', 'pink', 'orange', 'yellow', 'teal'];

interface ThemeValue {
  appearance: Appearance;
  themeColor: ThemeColor;
  setAppearance: (appearance: Appearance) => void;
  setThemeColor: (color: ThemeColor) => void;
}

const STORAGE_KEY = 'resonance.theme.v2';
const ThemeContext = createContext<ThemeValue | null>(null);

interface StoredTheme {
  appearance: Appearance;
  themeColor: ThemeColor;
}

function readTheme(): StoredTheme {
  const fallback: StoredTheme = { appearance: 'system', themeColor: 'default' };
  if (!loadSession()) return { ...fallback, appearance: readPublicAppearance() };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<StoredTheme>;
    return {
      appearance: parsed.appearance === 'light' || parsed.appearance === 'dark'
        ? parsed.appearance
        : 'system',
      themeColor: THEME_COLORS.includes(parsed.themeColor as ThemeColor)
        ? (parsed.themeColor as ThemeColor)
        : 'default',
    };
  } catch {
    return fallback;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<StoredTheme>(readTheme);
  const themeRef = useRef(theme);

  useEffect(() => {
    let requestVersion = 0;
    const loadServerTheme = (session: ReturnType<typeof loadSession>) => {
      if (!session) {
        requestVersion += 1;
        const publicTheme: StoredTheme = {
          appearance: readPublicAppearance(),
          themeColor: 'default',
        };
        themeRef.current = publicTheme;
        setTheme(publicTheme);
        return;
      }
      const version = ++requestVersion;
      if (session.bootstrap.theme) {
        themeRef.current = session.bootstrap.theme;
        setTheme(session.bootstrap.theme);
      }
      void api.getTheme().then((serverTheme) => {
        if (version !== requestVersion) return;
        themeRef.current = serverTheme;
        setTheme(serverTheme);
      }).catch(() => {
        /* The cached theme remains usable while offline. */
      });
    };

    loadServerTheme(loadSession());
    return subscribeToSession(loadServerTheme);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applyAppearance = () => {
      const resolved = theme.appearance === 'system'
        ? (media.matches ? 'dark' : 'light')
        : theme.appearance;
      root.dataset.appearance = resolved;
      root.style.colorScheme = resolved;
    };
    applyAppearance();
    root.dataset.themeColor = theme.themeColor;
    if (theme.appearance === 'system') media.addEventListener('change', applyAppearance);
    if (loadSession()) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
      } catch {
        /* Storage failures leave the applied theme intact for this session. */
      }
    } else {
      writePublicAppearance(theme.appearance);
    }
    return () => media.removeEventListener('change', applyAppearance);
  }, [theme]);

  const updateTheme = useCallback((next: StoredTheme) => {
    themeRef.current = next;
    setTheme(next);
    if (loadSession()) void api.updateTheme(next).catch(() => {
      /* Keep the optimistic local value; a later refresh reloads server state. */
    });
    else writePublicAppearance(next.appearance);
  }, []);

  const setAppearance = useCallback(
    (appearance: Appearance) => updateTheme({ ...themeRef.current, appearance }),
    [updateTheme],
  );
  const setThemeColor = useCallback(
    (themeColor: ThemeColor) => updateTheme({ ...themeRef.current, themeColor }),
    [updateTheme],
  );

  const value = useMemo(
    () => ({ ...theme, setAppearance, setThemeColor }),
    [theme, setAppearance, setThemeColor],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside a ThemeProvider.');
  return value;
}
